/**
 * Benvel Stylist Page — Story 11.3
 * Progressive enhancement: debounced form → outfit-grid SRA swap.
 *
 * Architecture:
 *   - AC2: NO client-side LLM call. NO client-side Storefront API call.
 *     The SRA call hits /pages/asesor-de-estilo with updated query params;
 *     Liquid renders the new outfit-grid section server-side.
 *   - AC3: On select `change` events (debounced 300ms), calls swapSection
 *     from Story 1.6 benvel-section-renderer-helpers.js.
 *   - Focus + scroll: preserved per Story 1.6 focus-restore policy.
 *   - aria-live="polite" on the grid root announces the update to screen readers.
 *   - No-JS fallback: form submits via GET (full page reload). This JS
 *     file is optional — removing it leaves the page fully functional.
 *
 * Import: consumed via Shopify importmap / module type script.
 * Budget: ≤ 2 KB gzipped (AC11 / Story 1.9).
 */

import { swapSection } from './benvel-section-renderer-helpers.js';

const DEBOUNCE_MS = 300;
const OUTFIT_GRID_SECTION_ID = 'benvel-stylist-outfit-grid';

/**
 * Debounce utility — delays fn until `wait` ms after last invocation.
 * @param {Function} fn
 * @param {number} wait
 * @returns {Function}
 */
function debounce(fn, wait) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Serialize the form into a URLSearchParams string, omitting empty values
 * (blank selects → no param in URL → adapter treats as "any").
 * @param {HTMLFormElement} form
 * @returns {string} query string
 */
function serializeForm(form) {
  const params = new URLSearchParams();
  for (const [name, value] of new FormData(form).entries()) {
    if (value && value.trim() !== '') {
      params.set(name, value);
    }
  }
  return params.toString();
}

/**
 * Trigger an outfit-grid swap when form params change.
 * @param {HTMLFormElement} form
 */
async function handleFormChange(form) {
  const qs = serializeForm(form);
  const url = form.action + (qs ? '?' + qs : '');

  try {
    await swapSection(OUTFIT_GRID_SECTION_ID, {
      url,
      source: 'stylist-input-form',
    });

    // AC3: emit telemetry event for analytics hooks
    document.dispatchEvent(
      new CustomEvent('benvel:stylist-query-changed', {
        bubbles: true,
        detail: { url, params: Object.fromEntries(new URLSearchParams(qs)) },
      })
    );
  } catch {
    // Failure is silent — the no-JS form submit fallback remains available.
    // Story 1.6 already dispatches benvel:network-error on failure.
  }
}

/**
 * Initialise the Stylist page progressive enhancement.
 * Safe to call multiple times (idempotent guard via data attribute).
 */
function init() {
  const form = document.querySelector('[data-stylist-form]');
  if (!form || form.dataset.stylistEnhanced) return;
  form.dataset.stylistEnhanced = 'true';

  const debouncedHandler = debounce(() => handleFormChange(form), DEBOUNCE_MS);

  // AC3: listen to change events on all selects in the form
  form.addEventListener('change', (event) => {
    if (event.target.tagName === 'SELECT') {
      debouncedHandler();
    }
  });
}

// Run after DOM is ready; safe in <head> defer or module position.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
